import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { PROPERTY_TYPES, ROOF_TYPES, ORIENTATIONS, YESNO } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const init = {
  first_name: "", last_name: "", email: "", phone: "", address: "",
  property_type: "", roof_type: "", roof_area: "", roof_orientation: "",
  annual_consumption: "", storage_wanted: "", wallbox_wanted: "",
  timeframe: "", notes: "",
};

export default function LeadNew() {
  const [data, setData] = useState(init);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.first_name || !data.last_name) {
      toast.error("Vor- und Nachname sind erforderlich");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        roof_area: data.roof_area ? parseFloat(data.roof_area) : null,
        annual_consumption: data.annual_consumption ? parseFloat(data.annual_consumption) : null,
      };
      const r = await api.post("/leads", payload);
      toast.success("Lead angelegt");
      nav(`/leads/${r.data.lead_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "lead_limit_reached") {
        toast.error(detail.message || "Leadlimit erreicht – bitte Upgrade anfragen.", { duration: 6000 });
        nav("/settings");
      } else {
        toast.error(typeof detail === "string" ? detail : "Fehler beim Speichern");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="lead-new-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
      </button>
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Neuer Lead
        </h1>
        <p className="mt-1 text-slate-600">Erfassen Sie eine neue PV-Anfrage</p>
      </header>

      <form onSubmit={submit} className="space-y-6">
        <Card className="border-slate-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Kontaktdaten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Vorname *"><Input data-testid="field-first-name" value={data.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
            <Field label="Nachname *"><Input data-testid="field-last-name" value={data.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
            <Field label="E-Mail"><Input data-testid="field-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Telefon"><Input data-testid="field-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <div className="md:col-span-2"><Field label="Adresse"><Input data-testid="field-address" value={data.address} onChange={(e) => set("address", e.target.value)} placeholder="Straße, PLZ Ort" /></Field></div>
          </div>
        </Card>

        <Card className="border-slate-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">PV-Angaben</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Immobilientyp"><SelectField testid="field-property-type" value={data.property_type} onChange={(v) => set("property_type", v)} options={PROPERTY_TYPES} /></Field>
            <Field label="Dachtyp"><SelectField testid="field-roof-type" value={data.roof_type} onChange={(v) => set("roof_type", v)} options={ROOF_TYPES} /></Field>
            <Field label="Dachfläche (m²)"><Input data-testid="field-roof-area" type="number" value={data.roof_area} onChange={(e) => set("roof_area", e.target.value)} /></Field>
            <Field label="Dachausrichtung"><SelectField testid="field-orientation" value={data.roof_orientation} onChange={(v) => set("roof_orientation", v)} options={ORIENTATIONS} /></Field>
            <Field label="Jährlicher Stromverbrauch (kWh)"><Input data-testid="field-consumption" type="number" value={data.annual_consumption} onChange={(e) => set("annual_consumption", e.target.value)} /></Field>
            <Field label="Speicher gewünscht"><SelectField testid="field-storage" value={data.storage_wanted} onChange={(v) => set("storage_wanted", v)} options={YESNO} /></Field>
            <Field label="Wallbox gewünscht"><SelectField testid="field-wallbox" value={data.wallbox_wanted} onChange={(v) => set("wallbox_wanted", v)} options={YESNO} /></Field>
            <Field label="Gewünschter Umsetzungszeitraum"><Input data-testid="field-timeframe" value={data.timeframe} onChange={(e) => set("timeframe", e.target.value)} placeholder="z.B. Q2 2026" /></Field>
          </div>
          <Field label="Bemerkungen">
            <Textarea data-testid="field-notes" rows={4} value={data.notes} onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav("/leads")}>Abbrechen</Button>
          <Button data-testid="save-lead-btn" type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-700 text-white">
            {submitting ? "Speichern …" : "Lead speichern"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const SelectField = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Auswählen …" /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);
